import { onMounted, ref } from 'vue';

import * as DataDogLogs from '~/lib/datadog/datadog';

export const BREAKPOINTS = {
  '2XL': 1536,
  LG: 1024,
  MD: 768,
  SM: 640,
  XL: 1280,
};

const resizeCallbacks = new Set();
const resizeFinishCallbacks = new Set();
const RESIZE_FINISH_DELAY = 500;
let resizeFinishTimeout = null;
let isResizing = false;
let listenerInitialized = false;

const browserWidth = ref(window.innerWidth);
const deviceWidth = ref(screen.width);
const isSmall = ref(browserWidth.value < BREAKPOINTS.SM);
const isMedium = ref(browserWidth.value >= BREAKPOINTS.SM && browserWidth.value < BREAKPOINTS.MD);
const isMobile = ref(browserWidth.value < BREAKPOINTS.MD);
const isDesktop = ref(browserWidth.value >= BREAKPOINTS.MD);
const isLarge = ref(browserWidth.value >= BREAKPOINTS.LG);
const isXLarge = ref(browserWidth.value >= BREAKPOINTS['2XL']);
const isTouch = ref(false);

const updateViewportState = () => {
  browserWidth.value = window.innerWidth;
  deviceWidth.value = screen.width;
  isSmall.value = browserWidth.value < BREAKPOINTS.SM;
  isMedium.value = browserWidth.value >= BREAKPOINTS.SM && browserWidth.value < BREAKPOINTS.MD;
  isLarge.value = browserWidth.value >= BREAKPOINTS.LG;
  isXLarge.value = browserWidth.value >= BREAKPOINTS['2XL'];
  isMobile.value = browserWidth.value < BREAKPOINTS.MD;
  isDesktop.value = browserWidth.value >= BREAKPOINTS.MD;
};

const handleResize = () => {
  updateViewportState();

  // Execute resize callbacks
  resizeCallbacks.forEach((callback) => {
    try {
      callback();
    } catch (error) {
      DataDogLogs.error('[useViewportSize] Resize callback error', {}, error);
    }
  });

  // Handle resizeFinish callbacks with debouncing
  if (!isResizing) {
    isResizing = true;
  }

  if (resizeFinishTimeout) {
    clearTimeout(resizeFinishTimeout);
  }

  resizeFinishTimeout = setTimeout(() => {
    isResizing = false;

    resizeFinishCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        DataDogLogs.error('[useViewportSize] ResizeFinish callback error', {}, error);
      }
    });
  }, RESIZE_FINISH_DELAY);
};

export function useViewportSize() {
  /**
   * Register callback for resize events
   * Fires during resize (not debounced)
   * @param {Function} callback - Function to call during resize
   * @returns {Function} Unregister function
   */
  const onResize = (callback) => {
    if (typeof callback !== 'function') {
      DataDogLogs.error(
        '[useViewportSize] onResize expects a function',
        { callbackType: typeof callback },
        new Error('Invalid callback type'),
      );
      return () => {};
    }

    resizeCallbacks.add(callback);

    return () => {
      resizeCallbacks.delete(callback);
    };
  };

  /**
   * Register callback for resize finish events
   * Fires after resize completes (debounced by 250ms)
   * @param {Function} callback - Function to call after resize completes
   * @returns {Function} Unregister function
   */
  const onResizeFinish = (callback) => {
    if (typeof callback !== 'function') {
      DataDogLogs.error(
        '[useViewportSize] onResizeFinish expects a function',
        { callbackType: typeof callback },
        new Error('Invalid callback type'),
      );
      return () => {};
    }

    resizeFinishCallbacks.add(callback);

    return () => {
      resizeFinishCallbacks.delete(callback);
    };
  };

  onMounted(() => {
    // Initialize listener only once globally (singleton pattern)
    if (!listenerInitialized) {
      isTouch.value = window.matchMedia('(pointer: coarse)').matches;
      window.addEventListener('resize', handleResize);
      updateViewportState();
      listenerInitialized = true;
    }
  });

  return {
    browserWidth,
    deviceWidth,
    isDesktop,
    isLarge,
    isMedium,
    isMobile,
    isSmall,
    isTouch,
    isXLarge,
    onResize,
    onResizeFinish,
  };
}
